import { useState, type ReactNode } from "react";
import { ListOrdered, Printer } from "lucide-react";

import { Button } from "@/components/ui/button";

export interface LegalTocItem {
  id: string;
  label: string;
}

interface LegalDocLayoutProps {
  title: string;
  preamble: string;
  lastUpdated: string;
  effectiveDate?: string;
  toc: LegalTocItem[];
  children: ReactNode;
}

/**
 * Shared layout for legal documents (/terms, /privacy, etc.).
 * Provides sticky ToC on desktop, floating ToC on mobile,
 * print-friendly styles, and dark-mode-aware typography.
 */
export function LegalDocLayout({
  title,
  preamble,
  lastUpdated,
  effectiveDate,
  toc,
  children,
}: LegalDocLayoutProps) {
  const [tocOpen, setTocOpen] = useState(false);

  return (
    <main className="bg-background min-h-screen">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; color: black !important; }
          main { background: white !important; }
          h1, h2, h3, p, li { color: black !important; font-size: 11pt !important; line-height: 1.6 !important; }
          h1 { font-size: 18pt !important; }
          h2 { font-size: 13pt !important; page-break-after: avoid; }
          .legal-section { page-break-inside: avoid; }
          a { color: black !important; text-decoration: none !important; }
        }
      `}</style>

      {/* Floating ToC toggle (mobile) */}
      <div className="no-print fixed bottom-6 right-6 z-40 lg:hidden">
        <Button
          size="icon"
          variant="secondary"
          className="h-12 w-12 rounded-full shadow-lg"
          onClick={() => setTocOpen((v) => !v)}
          aria-label="目次を開く"
        >
          <ListOrdered className="h-5 w-5" />
        </Button>
      </div>

      {tocOpen && (
        <div
          className="no-print fixed inset-0 z-30 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={() => setTocOpen(false)}
        >
          <div
            className="absolute bottom-0 left-0 right-0 max-h-[70vh] overflow-y-auto rounded-t-2xl border-t border-border bg-card p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="mb-4 text-lg font-semibold">目次</h2>
            <TocList items={toc} onNavigate={() => setTocOpen(false)} />
          </div>
        </div>
      )}

      <div className="mx-auto flex max-w-6xl gap-8 px-4 py-10 sm:px-6 lg:px-8">
        <aside className="no-print sticky top-10 hidden h-fit w-64 shrink-0 lg:block">
          <div className="rounded-lg border border-border bg-card/50 p-4">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
              <ListOrdered className="h-4 w-4" />
              目次
            </h2>
            <TocList items={toc} />
          </div>
        </aside>

        <article className="mx-auto w-full max-w-3xl">
          <header className="mb-8 border-b border-border pb-6">
            <div className="no-print mb-4 flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                最終更新日:{lastUpdated}
                {effectiveDate ? (
                  <>
                    <span className="mx-2">/</span>
                    施行日:{effectiveDate}
                  </>
                ) : null}
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => window.print()}
                className="gap-1.5"
              >
                <Printer className="h-3.5 w-3.5" />
                印刷
              </Button>
            </div>

            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{title}</h1>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{preamble}</p>
          </header>

          <div className="space-y-10">{children}</div>

          <footer className="mt-12 border-t border-border pt-6 text-xs text-muted-foreground">
            <p>最終更新日:{lastUpdated}</p>
            {effectiveDate ? <p>施行日:{effectiveDate}</p> : null}
            <p className="mt-2">以上</p>
          </footer>
        </article>
      </div>
    </main>
  );
}

function TocList({
  items,
  onNavigate,
}: {
  items: LegalTocItem[];
  onNavigate?: () => void;
}) {
  return (
    <ol className="space-y-1.5 text-sm">
      {items.map((item) => (
        <li key={item.id}>
          <a
            href={`#${item.id}`}
            onClick={onNavigate}
            className="block rounded px-2 py-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            {item.label}
          </a>
        </li>
      ))}
    </ol>
  );
}
